import * as React from 'react'
import { format } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { DateRange } from 'react-day-picker'
import { useFormContext } from 'react-hook-form'

import { cn } from '@/lib/utils'
import { Button } from '../ui/button'
import { Calendar } from '../ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { FormControl, FormItem, FormLabel, FormMessage } from '../ui/form'

type CalendarRangeFieldProps = {
    label?: string
    nameFrom: string
    nameTo: string
    disabled?: boolean
}

export default function CalendarRangeField({ label, nameFrom, nameTo, disabled }: CalendarRangeFieldProps) {
    const form = useFormContext()
    const fromVal = form.watch(nameFrom) as string | undefined
    const toVal = form.watch(nameTo) as string | undefined

    const selected: DateRange | undefined = (fromVal || toVal)
        ? {
            from: fromVal ? new Date(fromVal) : undefined,
            to: toVal ? new Date(toVal) : undefined,
        }
        : undefined

    const labelText = React.useMemo(() => {
        if (selected?.from && selected?.to) {
            return `${format(selected.from, 'PPP')} – ${format(selected.to, 'PPP')}`
        }
        if (selected?.from) return `${format(selected.from, 'PPP')} – …`
        return 'Pick a date range'
    }, [selected])

    return (
        <FormItem className='flex flex-col w-full'>
            {label && <FormLabel>{label}</FormLabel>}
            <Popover>
                <PopoverTrigger asChild>
                    <FormControl>
                        <Button
                            variant={'outline'}
                            className={cn('w-full justify-start text-left font-normal', !selected?.from && 'text-muted-foreground')}
                            disabled={disabled}
                        >
                            {labelText}
                            <CalendarIcon className='ml-auto h-4 w-4 opacity-50' />
                        </Button>
                    </FormControl>
                </PopoverTrigger>
                <PopoverContent className='w-auto p-0' align='start'>
                    <Calendar
                        mode='range'
                        numberOfMonths={2}
                        selected={selected}
                        onSelect={(range) => {
                            const fromIso = range?.from ? range.from.toISOString() : ''
                            const toIso = range?.to ? range.to.toISOString() : ''
                            form.setValue(nameFrom as any, fromIso, { shouldDirty: true })
                            form.setValue(nameTo as any, toIso, { shouldDirty: true })
                        }}
                        captionLayout='dropdown'
                        fromYear={2000}
                        toYear={2030}
                    />
                </PopoverContent>
            </Popover>
            <FormMessage />
        </FormItem>
    )
}


